import type { IInAppReferral } from '@commenda/types';
import { fetcher } from '@commenda/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inAppReferralsKeys } from './keys';
import { IN_APP_REFERRALS_ROUTE } from './routes';
import type { UpdateInAppReferralLeadParams } from './types';

const throwIfNotOk = async (res: Response, fallback: string) => {
  if (res.ok) return;
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  throw new Error(body.message || fallback);
};

export const useGenerateInAppReferral = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
    }: {
      companyId: number;
    }): Promise<IInAppReferral> => {
      const res = await fetcher(
        `${IN_APP_REFERRALS_ROUTE}/company/${companyId}`,
        { method: 'POST' }
      );
      await throwIfNotOk(res, 'Could not generate your referral link');
      return ((await res.json()) as { referral: IInAppReferral }).referral;
    },
    onSuccess: (_data, { companyId }) => {
      queryClient.invalidateQueries({
        queryKey: inAppReferralsKeys.forCompany(companyId),
      });
    },
  });
};

export const useUpdateInAppReferralLead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      leadId,
      params,
    }: {
      leadId: string;
      params: UpdateInAppReferralLeadParams;
    }): Promise<void> => {
      const res = await fetcher(`${IN_APP_REFERRALS_ROUTE}/leads/${leadId}`, {
        method: 'POST',
        body: params,
      });
      await throwIfNotOk(res, 'Could not update this lead');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: inAppReferralsKeys.adminLeadLists(),
      });
      queryClient.invalidateQueries({
        queryKey: inAppReferralsKeys.adminSummaries(),
      });
      queryClient.invalidateQueries({
        queryKey: inAppReferralsKeys.adminCompanyLists(),
      });
      queryClient.invalidateQueries({
        queryKey: inAppReferralsKeys.leadLists(),
      });
    },
  });
};
